import React, { useState } from 'react';
import parse from 'html-react-parser';
import OpcionItem from './OpcionItem';
import TipTapPreguntaEditor from '../../../../../../components/atoms/TipTapPreguntaEditor';
import ConfirmacionCancelarModal from '../../../UI/ConfirmacionCancelarModal';
import './PreguntaEditorInline.css';

/**
 * Componente que muestra una pregunta con capacidad de edición inline
 * Funciona como card en vista colapsada y como formulario en vista expandida
 * 
 * ACTUALIZADO: Migrado de react-draft-wysiwyg a TipTap
 * - Soporte completo de texto enriquecido
 * - Inserción y edición de imágenes con resize
 * - Drag & drop de imágenes
 * - Mejor UX y performance
 */
const PreguntaEditorInline = ({
  pregunta,
  isEditing,
  onStartEdit,
  onCancelEdit,
  onUpdatePregunta,
  onUpdateOpcion,
  onAgregarOpcion,
  onEliminarOpcion,
  onGuardarTodo,
  onEliminar,
  onDescartarRapido,
  guardandoPregunta,
  guardandoOpcion,
  evaluacionId,
  isEditMode,
  isPsicologicaSalamanca = false,
  hasPendingSave = false
}) => {
  const [showCancelarEdicionModal, setShowCancelarEdicionModal] = useState(false);
  const esPreguntaNueva = typeof pregunta.id === 'string' && pregunta.id.startsWith('nueva_');

  const renderTextoPreview = (html) => {
    if (!html) return <span style={{ color: '#999', fontStyle: 'italic' }}>Pregunta sin título</span>;
    // Remove images but keep all other formatting (bold, paragraphs, headings, etc.)
    const sinImagenes = html.replace(/<img[^>]*>/gi, '');
    if (!sinImagenes.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()) {
      return <span style={{ color: '#999', fontStyle: 'italic' }}>Pregunta sin título</span>;
    }
    return parse(sinImagenes);
  };

  const renderOpcionPreview = (html) => {
    if (!html) return '';
    const sinImagenes = html.replace(/<img[^>]*>/gi, '');
    return parse(sinImagenes);
  };

  const handleCancelAction = () => {
    if (hasPendingSave) {
      setShowCancelarEdicionModal(true);
    } else {
      onCancelEdit();
    }
  };

  // Vista colapsada - solo lectura
  if (!isEditing) {
    return (
      <>
        <div className="card mb-3 pregunta-item">
          <div className="card-header">
          <div className="d-flex align-items-start gap-4 mb-2">
            <span className="badge mt-1" style={{ fontSize: '0.9rem', fontWeight: '700', flexShrink: 0, backgroundColor: '#1e88e5', color: '#ffffff', padding: '6px 12px', borderRadius: '8px', marginRight: '12px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '30px' }}>
              {pregunta.numero}
            </span>
            <div className="pregunta-preview-content" style={{ fontSize: '0.95rem', color: '#444', lineHeight: '1.6', flex: 1, minWidth: 0 }}>
              {renderTextoPreview(pregunta.pregunta)}
            </div>
          </div>
          <div className="d-flex justify-content-end align-items-center gap-4 mt-3">
            <div className="d-flex gap-4" role="group">
              <button
                type="button"
                className="btn btn-sm"
                style={{ backgroundColor: '#1abc9c', border: 'none', color: 'white', fontWeight: 500, borderRadius: '6px' }}
                onClick={() => onStartEdit(pregunta)}
              >
                <i className="fas fa-edit me-1"></i>
                Editar
              </button>
              <button
                type="button"
                className="btn btn-sm"
                style={{ backgroundColor: '#e74c3c', border: 'none', color: 'white', fontWeight: 500, borderRadius: '6px' }}
                onClick={() => onEliminar(pregunta.id)}
              >
                <i className="fas fa-trash me-1"></i>
                Eliminar
              </button>
            </div>
          </div>
          </div>

          <div className="card-body" style={{ padding: '1.25rem' }}>
          {pregunta.opciones && pregunta.opciones.length > 0 && (
            <div className="opciones-preview">
              <small className="text-muted d-block mb-3" style={{ fontSize: '0.9rem', fontWeight: '600' }}>
                Opciones de respuesta:
              </small>
              <div className="row">
                {pregunta.opciones.map((opcion, idx) => (
                  <div key={opcion.id} className="col-md-6 mb-3">
                    <div className="d-flex align-items-center" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '10px 14px', gap: '12px' }}>
                      <span 
                        className="badge"
                        style={{ 
                          fontSize: '0.9rem', 
                          padding: '0.4rem 0.7rem', 
                          minWidth: '34px',
                          fontWeight: 'bold',
                          flexShrink: 0,
                          backgroundColor: '#28c76f',
                          color: '#ffffff'
                        }}
                      >
                        {String.fromCharCode(65 + idx)}
                      </span>
                      <small className="text-truncate flex-grow-1" style={{ fontSize: '0.9rem' }}>
                        {renderOpcionPreview(opcion.texto)}
                      </small>
                      <small className="fw-bold" style={{ minWidth: '55px', textAlign: 'right', color: '#28a745', fontSize: '0.9rem', flexShrink: 0 }}>
                        {opcion.puntaje || 0} pts
                      </small>
                    </div>
                  </div>
                ))}
              </div>
              {!isPsicologicaSalamanca && !pregunta.opciones.some(op => op.esCorrecta || parseFloat(op.puntaje) > 0) && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '12px', padding: '8px 12px', backgroundColor: '#fff5f5', borderRadius: '6px', border: '1px solid #f5c6cb' }}>
                  <i className="fas fa-exclamation-circle" style={{ color: '#dc3545', marginTop: '2px', flexShrink: 0 }}></i>
                  <small style={{ color: '#dc3545', fontWeight: 500, lineHeight: '1.5' }}>
                    Recordatorio: ninguna opción tiene puntaje asignado. Para que la evaluación califique correctamente, edita esta pregunta y coloca puntaje en la opción correcta.
                  </small>
                </div>
              )}
            </div>
          )}
          </div>
        </div>

        <ConfirmacionCancelarModal
          show={showCancelarEdicionModal}
          currentStep={1}
          totalSteps={4}
          title="Descartar cambios"
          icon="exclamation-triangle"
          iconColor="#dc3545"
          message="Tiene cambios sin guardar en esta pregunta. Si sale, los perderá."
          secondaryMessage="¿Desea continuar?"
          confirmLabel="Sí, continuar"
          cancelLabel="Volver"
          onConfirm={() => {
            setShowCancelarEdicionModal(false);
            onCancelEdit();
          }}
          onCancel={() => setShowCancelarEdicionModal(false)}
        />
      </>
    );
  }

  // Vista expandida - edición inline
  return (
    <>
      <div className="card mb-3 pregunta-item border-primary" style={{ borderLeftWidth: '4px' }}>
        <div className="card-header bg-light">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <span className="badge mr-3" style={{ fontSize: '0.9rem', fontWeight: '700', backgroundColor: '#1e88e5', color: '#ffffff', padding: '6px 12px', borderRadius: '8px', marginRight: '12px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '30px' }}>{pregunta.numero}</span>
            <span style={{ fontSize: '0.95rem', color: '#666' }}>Pregunta</span>
          </div>
          <div className="d-flex align-items-center gap-2">
            {esPreguntaNueva && (
              <button
                type="button"
                className="btn btn-sm step3-btn-delete-fast"
                onClick={onDescartarRapido}
                title="Descartar pregunta nueva"
                style={{ borderRadius: '8px', minWidth: '96px' }}
              >
                <i className="fas fa-trash me-1"></i>
                Eliminar
              </button>
            )}
          </div>
        </div>
        </div>

        <div className="card-body">
        <div className="mb-3">
          <label className="form-label">
            <strong>Texto de la Pregunta</strong>
            <span className="text-danger">*</span>
          </label>
          <TipTapPreguntaEditor
            value={pregunta.pregunta}
            handleChange={(content) => onUpdatePregunta({ pregunta: content })}
            placeholder="Escriba el texto de la pregunta..."
            expandedHeight="300px"
          />
        </div>

        {/* Opciones de respuesta */}
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="form-label mb-0">
              <strong>Opciones de Respuesta</strong>
            </label>
            <button
              type="button"
              className="step3-btn-option"
              onClick={onAgregarOpcion}
              title={isPsicologicaSalamanca ? 'No se pueden añadir más opciones en Salamanca y Personalidad' : 'Agregar opción'}
            >
              <i className="fas fa-plus me-1"></i>
              Agregar Opción
            </button>
          </div>

          {pregunta.opciones?.map((opcion, idx) => (
            <OpcionItem
              key={opcion.id}
              opcion={opcion}
              index={idx}
              lockAsCorrect={isPsicologicaSalamanca}
              lockPuntaje={isPsicologicaSalamanca}
              autoDesactivarConPuntajeCero={!isPsicologicaSalamanca}
              onUpdate={(cambios) => onUpdateOpcion(opcion.id, cambios)}
              onEliminar={() => onEliminarOpcion(opcion.id)}
              canDelete={!isPsicologicaSalamanca && pregunta.opciones.length > 2}
            />
          ))}
        </div>
        </div>

        {/* Footer con botones de acción */}
        <div className="card-footer bg-light d-flex justify-content-between align-items-center">
        <button
          type="button"
          className="btn btn-sm"
          style={{ backgroundColor: '#95a5a6', border: 'none', color: 'white', fontWeight: 500, borderRadius: '6px', padding: '8px 20px' }}
          onClick={handleCancelAction}
        >
          <i className="fas fa-times me-2"></i>
          Cancelar
        </button>
        <button
          type="button"
          className="btn btn-sm"
          style={{ backgroundColor: '#1abc9c', border: 'none', color: 'white', fontWeight: 500, borderRadius: '6px', padding: '8px 20px' }}
          onClick={onGuardarTodo}
          disabled={guardandoPregunta || guardandoOpcion !== null}
        >
          {(guardandoPregunta || guardandoOpcion !== null) ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Guardando...
            </>
          ) : (
            <>
              <i className="fas fa-save me-2"></i>
              Guardar
            </>
          )}
        </button>
        </div>
      </div>

      <ConfirmacionCancelarModal
        show={showCancelarEdicionModal}
        currentStep={1}
        totalSteps={4}
        title="Descartar cambios"
        icon="exclamation-triangle"
        iconColor="#dc3545"
        message="Tiene cambios sin guardar en esta pregunta. Si sale, los perderá."
        secondaryMessage="¿Desea continuar?"
        confirmLabel="Sí, continuar"
        cancelLabel="Volver"
        onConfirm={() => {
          setShowCancelarEdicionModal(false);
          onCancelEdit();
        }}
        onCancel={() => setShowCancelarEdicionModal(false)}
      />
    </>
  );
};

export default PreguntaEditorInline;
